// DOM-to-canvas snapshot helpers built on snapdom, which paints a native
// browser clone (so layout, gaps and borders come out exactly as on screen)
// and embeds web fonts. Same-origin iframes (chart/html widgets) are
// rasterized inline by snapdom itself; sandboxed Pyplan iframes (HTML
// interfaces) are asked to capture themselves through the shim postMessage
// protocol and are composited on top. Both go through the fidelity plugin
// (see snapshotFidelity.ts).

import { getSnapshotFidelityPlugin } from './snapshotFidelity'

// Marker attribute for elements that must never appear inside a snapshot —
// e.g. the chat's capture overlay carries it, so the frozen frame never
// contains the capture UI.
export const SCREEN_CAPTURE_IGNORE_ATTR = 'data-screen-capture-ignore'

// Marks iframes whose shim answers capture requests (HTML interfaces). Other
// sandboxed iframes are still asked, briefly, but cannot be waited for.
export const CAPTURE_SHIM_ATTR = 'data-pyplan-capture-shim'

// Default snapshot resolution: device pixels for crisp crops, capped to keep
// the canvas memory bounded on high-density screens.
const DEFAULT_MAX_SCALE = 2

// How long to wait for a Pyplan iframe to acknowledge a capture request. An
// iframe without the shim (or with a stale one) never answers, so this keeps
// the snapshot from stalling on it.
const SHIM_ACK_TIMEOUT_MS = 1500
// Once acknowledged, how long to wait for the actual capture. The first one
// also loads snapdom inside the iframe through the CDN proxy, so the budget
// is generous.
const SHIM_RESULT_TIMEOUT_MS = 6000

let shimRequestCounter = 0

export interface ShimCaptureOptions {
  // How long an iframe may take to acknowledge; longer when it may still be
  // loading (e.g. right after an agent edited the interface).
  ackTimeoutMs?: number
  // Upper bound the iframe waits for its widgets to finish loading before it
  // captures; 0 captures right away.
  settleMs?: number
  // Only iframes inside this element are asked (the part of the picture kept).
  scope?: Element
}

interface ShimCapture {
  image: HTMLImageElement
  settled: boolean
}

// Sandboxed Pyplan iframes (HTML interfaces use sandbox="allow-scripts", an
// opaque origin) cannot be read from the parent at all, so the capture is done
// from the inside: the iframe shim acknowledges `pyplan-capture` immediately
// and then answers `pyplan-capture-result` with a PNG of its own document
// (see the shim in HtmlInterfaceRenderer.tsx). Resolves null on timeout,
// failure or abort, so a misbehaving iframe never blocks the snapshot.
const requestShimCapture = (
  iframe: HTMLIFrameElement,
  backgroundColor: string,
  { ackTimeoutMs = SHIM_ACK_TIMEOUT_MS, settleMs = 0 }: ShimCaptureOptions,
  signal?: AbortSignal
): Promise<ShimCapture | null> =>
  new Promise((resolve) => {
    const win = iframe.contentWindow
    if (!win || signal?.aborted) {
      resolve(null)
      return
    }
    shimRequestCounter += 1
    const requestId = `capture-${shimRequestCounter}`
    let timer = 0
    const cleanup = () => {
      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      signal?.removeEventListener('abort', onAbort)
    }
    const fail = () => {
      cleanup()
      resolve(null)
    }
    const onAbort = () => fail()
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (event.source !== win || !data || data.requestId !== requestId) return
      if (data.type === 'pyplan-capture-ack') {
        // The shim is there and working on it: extend the wait.
        window.clearTimeout(timer)
        timer = window.setTimeout(fail, SHIM_RESULT_TIMEOUT_MS + settleMs)
        return
      }
      if (data.type !== 'pyplan-capture-result') return
      cleanup()
      if (typeof data.dataUrl !== 'string' || !data.dataUrl) {
        resolve(null)
        return
      }
      const image = new Image()
      image.onload = () => resolve({ image, settled: data.settled !== false })
      image.onerror = () => resolve(null)
      image.src = data.dataUrl
    }
    timer = window.setTimeout(fail, ackTimeoutMs)
    signal?.addEventListener('abort', onAbort)
    window.addEventListener('message', onMessage)
    win.postMessage({ type: 'pyplan-capture', requestId, backgroundColor, settleMs }, '*')
  })

const isIframeReadable = (iframe: HTMLIFrameElement): boolean => {
  try {
    return !!(iframe.contentDocument ?? iframe.contentWindow?.document)
  } catch {
    return false
  }
}

export interface ElementSnapshotOptions {
  backgroundColor: string
  // Output resolution multiplier. Defaults to the device pixel ratio (capped
  // at 2); pass 1 for large exports where memory matters more than crispness.
  scale?: number
  // Aborts the pending iframe shim round-trips (snapdom itself cannot be
  // interrupted, but it is bounded).
  signal?: AbortSignal
  shim?: ShimCaptureOptions
}

export interface ElementSnapshot {
  canvas: HTMLCanvasElement
  // Actual canvas pixels per CSS pixel of the captured element. Derived from
  // the output instead of trusting the requested scale, because snapdom's own
  // device-pixel-ratio handling varies across browsers.
  pixelRatio: number
  // Capture-shim iframes in view, how many painted themselves, and whether all
  // of them had finished loading.
  frames: { total: number; captured: number; settled: boolean }
}

// Renders an element (and everything inside it) into a canvas.
export const captureElementSnapshot = async (
  element: HTMLElement,
  { backgroundColor, scale, signal, shim = {} }: ElementSnapshotOptions
): Promise<ElementSnapshot> => {
  // Loaded on demand: snapdom is only needed once a capture starts.
  const { snapdom } = await import('@zumer/snapdom')
  const effectiveScale = scale ?? Math.min(window.devicePixelRatio || 1, DEFAULT_MAX_SCALE)

  const canvas = await snapdom.toCanvas(element, {
    scale: effectiveScale,
    // snapdom multiplies the device pixel ratio on top of `scale` by default,
    // which on retina displays quadruples the output (and desyncs it from
    // every coordinate computed here). `scale` is our only multiplier.
    dpr: 1,
    backgroundColor,
    embedFonts: true,
    exclude: [`[${SCREEN_CAPTURE_IGNORE_ATTR}]`],
    plugins: [getSnapshotFidelityPlugin()]
  })

  const elementRect = element.getBoundingClientRect()
  const pixelRatio = elementRect.width > 0 ? canvas.width / elementRect.width : effectiveScale
  const snapshot: ElementSnapshot = {
    canvas,
    pixelRatio,
    frames: { total: 0, captured: 0, settled: true }
  }

  const ctx = canvas.getContext('2d')
  // If the capture was cancelled while snapdom rendered, skip the iframe
  // round-trips: the result is going to be discarded anyway.
  if (!ctx || signal?.aborted) return snapshot
  const unreadableIframes = Array.from(element.getElementsByTagName('iframe')).filter((iframe) => {
    if (isIframeReadable(iframe)) return false
    if (shim.scope && !shim.scope.contains(iframe)) return false
    const rect = iframe.getBoundingClientRect()
    // Skip iframes that are hidden or fully outside the captured element.
    if (rect.width === 0 || rect.height === 0) return false
    return (
      rect.bottom >= elementRect.top &&
      rect.right >= elementRect.left &&
      rect.top <= elementRect.bottom &&
      rect.left <= elementRect.right
    )
  })

  const hasShim = (iframe: HTMLIFrameElement) => iframe.hasAttribute(CAPTURE_SHIM_ATTR)
  snapshot.frames.total = unreadableIframes.filter(hasShim).length
  await Promise.all(
    unreadableIframes.map(async (iframe) => {
      const shot = await requestShimCapture(
        iframe,
        backgroundColor,
        hasShim(iframe) ? shim : {},
        signal
      )
      if (hasShim(iframe)) {
        snapshot.frames.captured += shot ? 1 : 0
        snapshot.frames.settled = snapshot.frames.settled && !!shot?.settled
      }
      if (!shot) return
      // The canvas maps 1:1 to the element (times pixelRatio), so the
      // iframe's rect relative to the element gives its exact destination.
      const rect = iframe.getBoundingClientRect()
      const x = Math.round((rect.left - elementRect.left) * pixelRatio)
      const y = Math.round((rect.top - elementRect.top) * pixelRatio)
      const width = rect.width * pixelRatio
      const height = rect.height * pixelRatio
      // Resampling a near-identical size blurs all the text, so an image within
      // a pixel or two of its slot is drawn as is.
      const nearNatural =
        Math.abs(shot.image.width - width) <= 2 && Math.abs(shot.image.height - height) <= 2
      if (nearNatural) ctx.drawImage(shot.image, x, y)
      else ctx.drawImage(shot.image, x, y, width, height)
    })
  )

  return snapshot
}

// snapdom sizes its canvas to the full render of <body>, which can exceed the
// viewport when portals or overflowing elements extend the body's scroll
// size. A viewport snapshot must map 1:1 to the screen, so the canvas is
// cropped back to the viewport region.
const cropCanvasToViewport = (
  canvas: HTMLCanvasElement,
  scale: number,
  backgroundColor: string
): HTMLCanvasElement => {
  const targetWidth = Math.round(window.innerWidth * scale)
  const targetHeight = Math.round(window.innerHeight * scale)
  if (canvas.width === targetWidth && canvas.height === targetHeight) return canvas
  const cropped = document.createElement('canvas')
  cropped.width = targetWidth
  cropped.height = targetHeight
  const ctx = cropped.getContext('2d')
  if (!ctx) return canvas
  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, targetWidth, targetHeight)
  // The canvas starts at the body's border box: a negative offset (scroll) moves
  // where it is read from, a positive one (body margin) where it lands.
  const bodyRect = document.body.getBoundingClientRect()
  const sourceX = Math.max(0, Math.round(-bodyRect.left * scale))
  const sourceY = Math.max(0, Math.round(-bodyRect.top * scale))
  const destX = Math.max(0, Math.round(bodyRect.left * scale))
  const destY = Math.max(0, Math.round(bodyRect.top * scale))
  const width = Math.min(targetWidth - destX, canvas.width - sourceX)
  const height = Math.min(targetHeight - destY, canvas.height - sourceY)
  if (width > 0 && height > 0) {
    ctx.drawImage(canvas, sourceX, sourceY, width, height, destX, destY, width, height)
  }
  return cropped
}

export interface ViewportSnapshot {
  canvas: HTMLCanvasElement
  frames: ElementSnapshot['frames']
}

// Renders the currently visible viewport into a canvas.
export const captureViewportSnapshot = async (
  backgroundColor: string,
  signal?: AbortSignal,
  shim?: ShimCaptureOptions
): Promise<ViewportSnapshot> => {
  const { canvas, frames } = await captureElementSnapshot(document.body, {
    backgroundColor,
    signal,
    shim
  })
  // The crop scale comes from the document width, not the body's client width:
  // a body wider than the viewport would otherwise shrink the whole capture.
  const pageWidth = Math.max(
    document.body.getBoundingClientRect().width,
    document.documentElement.scrollWidth,
    document.body.scrollWidth
  )
  return { canvas: cropCanvasToViewport(canvas, canvas.width / pageWidth, backgroundColor), frames }
}
